import { useTranslation } from 'react-i18next';
import { useCurrentUserProfile } from '../lib/useCurrentUserProfile.js';
import { Button } from './ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog.js';

interface ResultLinkAccountDialogProps {
  open: boolean;
  /** Signs the current account out so the one the link was sent to can sign in; the link stays stashed. */
  onSwitchAccount: () => void;
  /** Drops the link and stays in the current account. */
  onDismiss: () => void;
}

/**
 * Shown when the link from a results mail is opened while a different account is signed in (typically a teacher on
 * the same computer). Opening the result in that account would only show an empty class list, so it asks instead.
 */
export function ResultLinkAccountDialog({ open, onSwitchAccount, onDismiss }: ResultLinkAccountDialogProps) {
  const { t } = useTranslation();
  const { teacher, student } = useCurrentUserProfile();
  const currentEmail = (teacher ?? student)?.email;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('ResultLink.wrongAccountTitle', { defaultValue: 'Dieser Link ist für ein anderes Konto' })}
          </DialogTitle>
          <DialogDescription>
            {currentEmail
              ? t('ResultLink.wrongAccountDescWithEmail', {
                  email: currentEmail,
                  defaultValue:
                    'Du bist gerade als {{email}} angemeldet. Die Ergebnisse aus der E-Mail gehören zu einem anderen Konto. Melde dich mit dem Konto an, an das die E-Mail ging, um sie zu sehen.',
                })
              : t('ResultLink.wrongAccountDesc', {
                  defaultValue:
                    'Die Ergebnisse aus der E-Mail gehören zu einem anderen Konto. Melde dich mit dem Konto an, an das die E-Mail ging, um sie zu sehen.',
                })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onDismiss}>
            {t('ResultLink.stayHere', { defaultValue: 'Hier bleiben' })}
          </Button>
          <Button onClick={onSwitchAccount} className="bg-brand-500 text-white">
            {t('ResultLink.switchAccount', { defaultValue: 'Abmelden und anderes Konto nutzen' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
